import React, { useEffect, useRef } from 'react';
import PhotographyServices from './PhotographyServices';
import PortfoliosSection from './PortfoliosSection';
import AboutUsSection from './AboutUsSection';
import HeroSlider from './HeroSlider';
import ImaageGallery3D from './ImageGallery3D';
import PortfolioSlider from './PortfolioSlider';
import Sliders from './Sliders';

import { Helmet } from "react-helmet-async";

const HomeSection = () => {
    // 1. Create mutable references for the DOM elements
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        // 2. Fetch the current underlying elements safely from the refs
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        // Safety Guard Clause: If elements aren't mounted yet, stop early.
        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.6;
        const speedRight = 0.4;

        // Hover handlers
        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        // Animation Loop Logic
        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        // 3. CLEANUP
        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>
            {/* SEO Metadata using React Helmet Async */}
            <Helmet>
                <title>KhultStudios | Best Photography & Videography Studio in Hyderabad</title>
                <meta
                    name="description"
                    content="KhultStudios is Hyderabad's premier photography and videography studio specializing in Wedding Photography, Pre Wedding Shoots, Acting & Modelling Portfolios, Fashion Photography, Product Photography, Corporate Photography, Commercial Photography, Event Photography, Maternity, Baby, Family Photography, Interior Photography, and Cinematic Films."
                />
                <meta
                    name="keywords"
                    content="KhultStudios, Photography Studio Hyderabad, Wedding Photography, Pre Wedding Photography, Fashion Photography, Acting Photography, Modelling Portfolio, Product Photography, Commercial Photography, Corporate Photography, Event Photography, Baby Photography, Child Photography, Maternity Photography, Family Photography, Interior Photography, Creative Photography, Videography, Drone Photography, Professional Photographer Hyderabad"
                />
                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />
                <link rel="canonical" href="https://khultstudios.com/" />
                
                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://khultstudios.com/" />
                <meta property="og:title" content="KhultStudios | Best Photography & Videography Studio in Hyderabad" />
                <meta property="og:description" content="Professional Wedding, Fashion, Product, Corporate, Event, Baby & Creative Photography and Cinematic Videography in Hyderabad." />
                <meta property="og:image" content="https://khultstudios.com/images/logo-khult.png" />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="KhultStudios | Best Photography & Videography Studio" />
                <meta name="twitter:description" content="Professional Wedding, Fashion, Product, Corporate, Event, Baby Photography & Cinematic Videography in Hyderabad." />
                <meta name="twitter:image" content="https://khultstudios.com/images/logo-khult.png" />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>

            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/khultstudio.mp4" type="video/mp4" />
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Welcome to KhultStudio
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Capturing Life's Most Beautiful Stories.
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudios, we believe every moment has a story worth telling. We specialize in capturing life's most meaningful experiences through creative photography and cinematic visuals. From pre-wedding and wedding shoots to fashion, modelling, acting portfolios, commercial campaigns, industrial projects, product photography, celebrity posters, events, and artistic concepts, our team combines creativity, passion, and precision to deliver timeless visuals.
                                    </p>

                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Whether you're building a personal brand, celebrating a milestone, or showcasing your business, KhultStudios transforms your vision into stunning images that leave a lasting impression.
                                    </p>
                                    <a
                                        href="about-us"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        About Us
                                    </a>
                                    {' | '}
                                    <a
                                        href="contact-us"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Contact Us
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-youtube" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/pre-wedding-and-wedding-photography.jpg" alt="Wedding 1" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/modelling-and-acting-photography.jpg" alt="Wedding 2" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/events-and-celebrations-photography.jpg" alt="Wedding 2" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/events-and-celebrations-photography1.jpg" alt="Wedding 2" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/maternity-and-baby-photography.jpg" alt="Wedding 2" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/maternity-and-baby-photography1.jpg" alt="Wedding 1" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/arts.jpg" alt="Wedding 2" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/movie-posters.jpg" alt="Wedding 2" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/products-and-brands-photography.jpg" alt="Wedding 2" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/commercial-and-industrial-photography.jpg" alt="Wedding 2" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <AboutUsSection />
            <HeroSlider />
            <PortfoliosSection />
            <ImaageGallery3D />
            <Sliders />
        </div>
    );
};

export default HomeSection;